import * as amplitude from "@amplitude/analytics-browser";
import { FunctionComponent, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { CustomQuestionAnswersByQuestionId } from "src/schemas/CustomQuestionType";
import { formatNestedQuestionAsProps } from "src/services/formTemplate/customQuestion";
import { isNotNull } from "src/services/predicates";
import { Form } from "src/services/url/Parent";
import * as AF from "src/types/formTemplate";
import {
  useSaveCustomQuestionAnswers,
  useUpdatePersonAnswerBankMutation,
} from "../api";
import { useCustomQuestionContext } from "../context/CustomQuestionContext";
import {
  computeDigestFromQuestionsAndAnswers,
  mapBankAnswersToFormAnswers,
} from "../helpers";
import { AnswerBankRecord } from "../list/schemas/AnswerBank";
import { AnswerBankEntryForm } from "./AnswerBankEntryForm";
import {
  formatInitialValues,
  formatInsertPersonAnswersWithAnswerBankIdPayload,
} from "./helpers";

type EditAnswerBankEntryFormProps = {
  initialAnswer: AnswerBankRecord;
};

export const EditAnswerBankEntryForm: FunctionComponent<
  EditAnswerBankEntryFormProps
> = (props) => {
  const { initialAnswer } = props;
  const { answerBankId } = initialAnswer;

  const {
    organization,
    customQuestion,
    customQuestionType,
    sourceIdCloneIdMapping,
    formId,
    setShowAnswerBank,
    setAnswerToEdit,
    answerBankRemoteData,
  } = useCustomQuestionContext();

  const { step = "" } = useParams();
  const navigate = useNavigate();

  const toast = useAvelaToast({
    position: "bottom",
    containerStyle: { marginBottom: 16 },
    isClosable: true,
  });

  const updatePersonAnswerBankEntry = useUpdatePersonAnswerBankMutation();
  const upsertCustomQuestionAnswers = useSaveCustomQuestionAnswers();

  const initialValues = formatInitialValues(
    initialAnswer,
    sourceIdCloneIdMapping
  );

  const fieldQuestions: AF.ClonedQuestion<AF.WithId>[] = useMemo(() => {
    return customQuestionType.custom_question_type_fields
      .map((field) => {
        return formatNestedQuestionAsProps(field.question);
      })
      .filter(isNotNull);
  }, [customQuestionType.custom_question_type_fields]);

  const onSubmitHandler = useCallback(
    async (values: CustomQuestionAnswersByQuestionId) => {
      try {
        amplitude.track("Save entry clicked", {
          cqt_name: customQuestion.question,
        });

        const digest = computeDigestFromQuestionsAndAnswers(
          fieldQuestions,
          values
        );

        // Check if an entry with the same digest already exists
        let finalAnswerBankId: uuid = answerBankId;
        if (answerBankRemoteData.hasData()) {
          const matchingRecord = answerBankRemoteData.data.find(
            (record: AnswerBankRecord) => record.digest === digest
          );
          if (matchingRecord) {
            finalAnswerBankId = matchingRecord.answerBankId;
          }
        }

        // Update the original entry if no other entry with the same digest was found
        if (finalAnswerBankId === answerBankId) {
          await updatePersonAnswerBankEntry({
            variables: {
              answer_bank_id: answerBankId,
              last_used_at: new Date().toISOString(),
              person_answers: formatInsertPersonAnswersWithAnswerBankIdPayload({
                answerBankId,
                answersByQuestionId: values,
                fieldQuestions,
              }),
            },
          });
        }

        await upsertCustomQuestionAnswers({
          formId,
          customQuestion,
          answersByQuestionId: mapBankAnswersToFormAnswers(
            values,
            sourceIdCloneIdMapping
          ),
          answerBankId: finalAnswerBankId,
        });
        toast({
          title: `${customQuestion.question} saved`,
        });
        setShowAnswerBank(true);
        setAnswerToEdit(null);
        navigate(Form.edit(organization, formId, parseInt(step, 10)), {
          state: { questionId: customQuestion.id },
        });
      } catch (error) {
        console.error(error);
        toast({
          title: `Error saving ${customQuestion.question}`,
          description:
            "Please try again later or report the problem to our support team.",
          status: "error",
        });
      }
    },
    [
      updatePersonAnswerBankEntry,
      answerBankId,
      fieldQuestions,
      upsertCustomQuestionAnswers,
      formId,
      customQuestion,
      sourceIdCloneIdMapping,
      toast,
      setShowAnswerBank,
      setAnswerToEdit,
      navigate,
      organization,
      step,
      answerBankRemoteData,
    ]
  );

  return (
    <AnswerBankEntryForm
      initialValues={initialValues}
      onSubmitHandler={onSubmitHandler}
    />
  );
};
